import useTierInfo from '../../../../infrastructure/hooks/useTierInfo';
import nl2br from 'react-nl2br';
import useUpgradeAction from '@/infrastructure/hooks/useUpgradeAction';
import {
  Alert,
  AlertTitle,
  Box,
  BoxProps,
  Button,
  styled,
  SxProps,
  Typography,
} from '@mui/material';
import { ArrowForward, OpenInNew } from '@mui/icons-material';
import { usePublicFeatureFlag } from '@/infrastructure/hooks/useFeatureFlags';
import { RequireEveryOrNone } from '@lemontaps/lib/types/util';
import { PropsWithChildren } from 'react';
import { useAppTranslation } from '@/infrastructure/hooks/useAppTranslation';
import { useIsDesktopMui } from '@/infrastructure/hooks/useIsDesktopMui';
import usePrivateThemeConfig from '@/infrastructure/hooks/usePrivateThemeConfig';
import { THEME_CONFIG_KEYS } from '@lemontaps/lib/constants';

type Props = RequireEveryOrNone<
  PropsWithChildren<{
    text: string;
    condition?: boolean;
    imageSrc?: string;
    imageHref?: string;
    imageSx?: SxProps;
    fullWidth?: boolean;
    helpArticleKey?: string; // we actually want this to be required
  }>,
  'children' | 'condition'
> &
  BoxProps;

export default function FeatureTeaser({
  fullWidth,
  condition,
  text,
  imageSrc,
  imageHref,
  imageSx,
  children,
  helpArticleKey,
  ...rest
}: Props) {
  const { upgradeAction, upgradeRequestPopup } = useUpgradeAction();

  const isDesktop = useIsDesktopMui();

  const { config: helpUrls } = usePrivateThemeConfig<Record<string, string>>(
    THEME_CONFIG_KEYS.HELP_RESOURCE_URLS,
  );
  const helpArticleUrl =
    typeof helpArticleKey === 'string' && helpUrls && helpArticleKey in helpUrls
      ? helpUrls[helpArticleKey]
      : undefined;

  return (
    <>
      {upgradeRequestPopup}
      <Box
        mt={2}
        mx={fullWidth ? 0 : isDesktop ? 4 : 2}
        display='flex'
        flexDirection='column'
        gap={1}
        {...rest}
      >
        {typeof condition !== 'undefined' ? (
          condition ? (
            <TeaserAlert
              upgradeHandler={upgradeAction}
              text={text}
              helpArticleUrl={helpArticleUrl}
            />
          ) : null
        ) : (
          <TeaserAlert upgradeHandler={upgradeAction} text={text} helpArticleUrl={helpArticleUrl} />
        )}
        {imageSrc && (
          <ImageWrapper
            {...(imageHref ? { component: 'a', href: imageHref, target: '_blank' } : {})}
            sx={imageSx}
          >
            <Box component='img' src={imageSrc} alt={text} sx={{ objectFit: 'contain' }} />
          </ImageWrapper>
        )}
        {typeof condition !== 'undefined' ? (
          condition ? (
            <Box
              sx={{
                mt: 2,
                opacity: 0.35,
                pointerEvents: 'none !important',
              }}
            >
              {children}
            </Box>
          ) : (
            children
          )
        ) : null}
      </Box>
    </>
  );
}

const TeaserAlert = ({
  text,
  upgradeHandler,
  helpArticleUrl,
}: {
  text: string;
  upgradeHandler: () => void;
  helpArticleUrl?: string;
}) => {
  const flag_hidePrices = usePublicFeatureFlag('sales_hidePrices');
  const { isTrial } = useTierInfo();
  const { t } = useAppTranslation();

  const isDesktop = useIsDesktopMui();

  const upgradeButton = (
    <Button
      variant='outlined'
      onClick={upgradeHandler}
      endIcon={<ArrowForward />}
      size='small'
      color='primary'
    >
      {isTrial
        ? t('upgradeTeaser.enableForTrial')
        : flag_hidePrices
          ? t('contactSales')
          : t('upgradeNow')}
    </Button>
  );

  return (
    <>
      <Alert
        sx={{
          '.LtAlert-message': {
            flex: 1,
          },
        }}
        severity={'info'}
        variant='outlined'
        action={
          (helpArticleUrl || import.meta.env.REACT_APP_PRODUCTION !== 'true') && (
            <Button
              component='a'
              href={helpArticleUrl}
              target='_blank'
              variant='text'
              startIcon={<OpenInNew />}
              color='secondary'
              size='small'
            >
              {t('learnMore')}
            </Button>
          )
        }
      >
        <AlertTitle>
          <Typography>{t('upgradeTeaser.upgradeYourPlan')}</Typography>
        </AlertTitle>

        <Typography variant='body2'>{nl2br(text)}</Typography>
        {isDesktop && <Box mt={1}>{upgradeButton}</Box>}
      </Alert>
      {!isDesktop && (
        <Box ml='auto' mt={1}>
          {upgradeButton}
        </Box>
      )}
    </>
  );
};

const ImageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
  px: '5rem',
  img: {
    maxWidth: 'min(80%, 78rem)',
    filter: 'blur(0px) grayscale(0.5)',
  },

  [theme.breakpoints.down('md')]: {
    px: 0,
    marginTop: 2,
    img: {
      maxWidth: '100%',
    },
  },
}));
